import React, { useState, useCallback, useRef } from 'react';
import {
  Upload, Download, AlertCircle, FileImage, RotateCcw, Loader2,
  CheckCircle2, XCircle, Plus, Trash2, Info, ArrowRightLeft
} from 'lucide-react';

type OutputFormat = 'png' | 'jpg' | 'webp';
type ConversionDirection = 'from-webp' | 'to-webp';

interface FileEntry {
  id: string;
  file: File;
  preview: string;
  resultUrl: string | null;
  resultSize: number;
  status: 'ready' | 'converting' | 'done' | 'error';
  error: string | null;
  isAnimated: boolean;
}

const WebpConverter: React.FC = () => {
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [direction, setDirection] = useState<ConversionDirection>('from-webp');
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [quality, setQuality] = useState(92);
  const [lossless, setLossless] = useState(false);
  const [bgColor, setBgColor] = useState('#ffffff');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const formatSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const checkAnimatedWebp = async (file: File): Promise<boolean> => {
    if (!file.type.includes('webp')) return false;
    try {
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      // Look for ANIM chunk in WebP
      for (let i = 0; i < Math.min(bytes.length - 4, 1024); i++) {
        if (bytes[i] === 0x41 && bytes[i + 1] === 0x4E &&
            bytes[i + 2] === 0x49 && bytes[i + 3] === 0x4D) {
          return true;
        }
      }
    } catch {
      // ignore
    }
    return false;
  };

  const getAcceptTypes = () => {
    if (direction === 'from-webp') return '.webp,image/webp';
    return 'image/jpeg,image/png,image/bmp,.jpg,.jpeg,.png,.bmp';
  };

  const getEffectiveOutputFormat = (): OutputFormat => {
    if (direction === 'to-webp') return 'webp';
    return outputFormat;
  };

  const handleFileSelect = useCallback(async (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    setError(null);

    const newEntries: FileEntry[] = [];
    for (let i = 0; i < selectedFiles.length; i++) {
      const f = selectedFiles[i];
      if (!f.type.startsWith('image/')) continue;

      const id = `${Date.now()}-${i}`;
      const preview = URL.createObjectURL(f);
      const isAnimated = await checkAnimatedWebp(f);

      newEntries.push({
        id, file: f, preview, isAnimated,
        resultUrl: null, resultSize: 0,
        status: 'ready', error: null,
      });
    }

    if (newEntries.length === 0) {
      setError('Please select valid image files.');
      return;
    }

    setFiles(prev => [...prev, ...newEntries]);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const convertFile = useCallback((entry: FileEntry) => {
    setFiles(prev => prev.map(f =>
      f.id === entry.id ? { ...f, status: 'converting', error: null, resultUrl: null } : f
    ));

    const img = new window.Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const fmt = getEffectiveOutputFormat();

      // Fill background for JPG (no transparency)
      if (fmt === 'jpg') {
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      ctx.drawImage(img, 0, 0);

      const mimeMap: Record<OutputFormat, string> = {
        png: 'image/png',
        jpg: 'image/jpeg',
        webp: 'image/webp',
      };

      let q: number | undefined;
      if (fmt === 'png') {
        q = undefined;
      } else if (fmt === 'webp' && lossless) {
        q = 1.0;
      } else {
        q = quality / 100;
      }

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const resultUrl = URL.createObjectURL(blob);
            setFiles(prev => prev.map(f =>
              f.id === entry.id ? { ...f, resultUrl, resultSize: blob.size, status: 'done' } : f
            ));
          } else {
            setFiles(prev => prev.map(f =>
              f.id === entry.id ? { ...f, status: 'error', error: 'Conversion failed' } : f
            ));
          }
        },
        mimeMap[fmt],
        q
      );
    };
    img.onerror = () => {
      setFiles(prev => prev.map(f =>
        f.id === entry.id ? { ...f, status: 'error', error: 'Failed to load image' } : f
      ));
    };
    img.src = entry.preview;
  }, [outputFormat, quality, bgColor, lossless, direction]);

  const convertAll = useCallback(() => {
    files.filter(f => f.status === 'ready').forEach(entry => convertFile(entry));
  }, [files, convertFile]);

  const downloadFile = (entry: FileEntry) => {
    if (!entry.resultUrl) return;
    const link = document.createElement('a');
    link.href = entry.resultUrl;
    const name = entry.file.name.replace(/\.[^.]+$/, '');
    const fmt = getEffectiveOutputFormat();
    link.download = `${name}.${fmt}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAll = () => {
    files.filter(f => f.status === 'done').forEach(entry => downloadFile(entry));
  };

  const removeFile = (id: string) => {
    setFiles(prev => {
      const entry = prev.find(f => f.id === id);
      if (entry) {
        URL.revokeObjectURL(entry.preview);
        if (entry.resultUrl) URL.revokeObjectURL(entry.resultUrl);
      }
      return prev.filter(f => f.id !== id);
    });
  };

  const reset = () => {
    files.forEach(f => {
      URL.revokeObjectURL(f.preview);
      if (f.resultUrl) URL.revokeObjectURL(f.resultUrl);
    });
    setFiles([]);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const switchDirection = (newDir: ConversionDirection) => {
    if (newDir === direction) return;
    reset();
    setDirection(newDir);
    if (newDir === 'to-webp') {
      setOutputFormat('webp');
    } else {
      setOutputFormat('png');
    }
  };

  const readyCount = files.filter(f => f.status === 'ready').length;
  const doneCount = files.filter(f => f.status === 'done').length;
  const hasAnimated = files.some(f => f.isAnimated);
  const fmt = getEffectiveOutputFormat();

  return (
    <div className="space-y-6">
      <canvas ref={canvasRef} className="hidden" />

      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-teal-100 rounded-full">
            <FileImage className="w-8 h-8 text-teal-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">WebP Converter</h2>
        <p className="text-gray-600 max-w-lg mx-auto">
          Convert images to and from WebP format. Batch processing supported, all done in your browser.
        </p>
      </div>

      {/* Direction tabs */}
      <div className="flex bg-gray-100 rounded-xl p-1">
        <button
          onClick={() => switchDirection('from-webp')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            direction === 'from-webp'
              ? 'bg-white text-teal-700 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <ArrowRightLeft className="w-4 h-4" />
          WebP to PNG/JPG
        </button>
        <button
          onClick={() => switchDirection('to-webp')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            direction === 'to-webp'
              ? 'bg-white text-teal-700 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <ArrowRightLeft className="w-4 h-4" />
          PNG/JPG to WebP
        </button>
      </div>

      {/* Upload */}
      {files.length === 0 ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-teal-400 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Drop {direction === 'from-webp' ? 'WebP' : 'PNG/JPG'} images here or click to select
          </h3>
          <p className="text-gray-500 text-sm">Select multiple files for batch conversion</p>
          <input
            ref={fileInputRef}
            type="file"
            accept={getAcceptTypes()}
            multiple
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Options */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {direction === 'from-webp' && (
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Output Format</label>
                  <div className="flex gap-2">
                    {([['png', 'PNG'], ['jpg', 'JPG'], ['webp', 'WebP']] as const).map(([val, label]) => (
                      <button
                        key={val}
                        onClick={() => setOutputFormat(val)}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                          outputFormat === val
                            ? 'bg-teal-600 text-white'
                            : 'bg-white border border-gray-200 text-gray-700 hover:border-teal-300'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {fmt !== 'png' && (
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">Quality</label>
                    <span className="text-lg font-bold text-teal-600">{lossless ? 'Lossless' : `${quality}%`}</span>
                  </div>
                  {!lossless && (
                    <input
                      type="range" min="1" max="100" value={quality}
                      onChange={(e) => setQuality(parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                    />
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-4">
              {fmt === 'webp' && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <div className="relative inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={lossless}
                      onChange={(e) => setLossless(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600"></div>
                  </div>
                  <span className="text-sm text-gray-700">Lossless WebP</span>
                </label>
              )}
              {fmt === 'jpg' && (
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-700">Background:</label>
                  <input
                    type="color"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
                  />
                  <span className="text-xs text-gray-500">For transparent areas</span>
                </div>
              )}
            </div>
          </div>

          {/* Animated WebP notice */}
          {hasAnimated && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-sm text-amber-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Animated WebP detected. Only the first frame will be exported.</span>
            </div>
          )}

          {/* File list */}
          <div className="space-y-3">
            {files.map(entry => (
              <div key={entry.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
                <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                  <img src={entry.preview} alt="" className="w-full h-full object-cover" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{entry.file.name}</p>
                    {entry.isAnimated && (
                      <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Animated</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>{formatSize(entry.file.size)}</span>
                    <span>{entry.file.type.split('/')[1]?.toUpperCase()}</span>
                    {entry.status === 'converting' && (
                      <span className="text-blue-600 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> Converting...
                      </span>
                    )}
                    {entry.status === 'done' && (
                      <span className="text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        {fmt.toUpperCase()}: {formatSize(entry.resultSize)}
                        {' '}({entry.resultSize < entry.file.size
                          ? `${Math.round((1 - entry.resultSize / entry.file.size) * 100)}% smaller`
                          : `${Math.round((entry.resultSize / entry.file.size - 1) * 100)}% larger`})
                      </span>
                    )}
                    {entry.status === 'error' && (
                      <span className="text-red-600 flex items-center gap-1">
                        <XCircle className="w-3 h-3" /> {entry.error}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {entry.status === 'done' && (
                    <button
                      onClick={() => downloadFile(entry)}
                      className="p-2 rounded-lg hover:bg-green-100 text-green-600 transition-colors"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => removeFile(entry.id)}
                    className="p-2 rounded-lg hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Add more */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-teal-300 hover:text-teal-600 transition-colors"
          >
            <Plus className="w-4 h-4" /> Add more files
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={getAcceptTypes()}
            multiple
            onChange={(e) => { handleFileSelect(e.target.files); if (fileInputRef.current) fileInputRef.current.value = ''; }}
            className="hidden"
          />

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3">
            {readyCount > 0 && (
              <button
                onClick={convertAll}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 text-white font-medium rounded-xl hover:bg-teal-700 transition-colors"
              >
                <FileImage className="w-5 h-5" />
                Convert {readyCount > 1 ? `All (${readyCount})` : ''} to {fmt.toUpperCase()}
              </button>
            )}
            {doneCount > 1 && (
              <button
                onClick={downloadAll}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 transition-colors"
              >
                <Download className="w-5 h-5" />
                Download All ({doneCount})
              </button>
            )}
            <button
              onClick={reset}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-300 transition-colors"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}
    </div>
  );
};

export default WebpConverter;
